import React, { useState, useEffect } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import {
    Box,
    Typography,
    Grid,
    Pagination,
    CircularProgress,
    Fade,
    Container,
    Paper,
    InputBase,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    StorefrontRounded,
    ArrowBackRounded,
    SearchRounded,
    CloseRounded,
    FilterListRounded,
} from "@mui/icons-material";
import { Footer } from "../../components/Layout/Footer";
import { Header1 } from "../../components/Layout/Header1.js";
import CardProduct from "../../components/Products/CardProduct";
import MethodGet from "../../config/Service";
import FilterPanel from "../Catalogo/FilterPanel";
import SEO from "../../components/SEO/SEO";

const BrandPage = () => {
    const { brandSlug } = useParams();
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Data states
    const [brand, setBrand] = useState(null);
    const [brands, setBrands] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter states
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [priceRange, setPriceRange] = useState([0, 10000]);
    const [debouncedPriceRange, setDebouncedPriceRange] = useState([0, 10000]);
    const [isPriceRangeManuallySet, setIsPriceRangeManuallySet] = useState(false);
    const [sortBy, setSortBy] = useState("default");
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);

    // Search state
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [page, setPage] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(24);

    // Debounce helpers
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedPriceRange(priceRange), 500);
        return () => clearTimeout(timer);
    }, [priceRange]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1);
        }, 600);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Load Initial Data
    useEffect(() => {
        // Brands
        MethodGet("/distribucion/products/brands")
            .then((res) => {
                const brandsData = res.data || [];
                setBrands(brandsData);
                const found = brandsData.find(
                    (b) => b.name.toLowerCase().replace(/\s+/g, "-") === brandSlug
                );
                if (found) {
                    setBrand(found);
                    document.title = `${found.name} - MerryColor`;
                }
            })
            .catch((err) => console.error(err));

        // Categories
        MethodGet("/cate")
            .then((res) => {
                setCategories(res.data.categories);
                const allSubs = res.data.categories.reduce((acc, cat) => {
                    return cat.subcategories ? [...acc, ...cat.subcategories] : acc;
                }, []);
                setSubcategories(allSubs);
            })
            .catch((err) => console.error(err));
    }, [brandSlug]);

    // Load Products
    useEffect(() => {
        if (!brand) return;
        setLoading(true);

        const params = new URLSearchParams();
        params.set("page", page);
        params.set("per_page", itemsPerPage);
        params.set("brand_id", brand.id);

        // Filters
        if (selectedCategory) params.set("category_id", selectedCategory);
        if (selectedSubcategory) params.set("subcategory_id", selectedSubcategory);
        if (sortBy !== "default") params.set("sort_by", sortBy);
        if (debouncedSearch) params.set("search", debouncedSearch);
        if (isPriceRangeManuallySet) {
            params.set("min_price", debouncedPriceRange[0]);
            params.set("max_price", debouncedPriceRange[1]);
        }

        MethodGet(`/distribucion/products/all?${params.toString()}`)
            .then((res) => {
                setProducts(res.data.data || res.data || []);
                setTotalProducts(
                    res.data.total || (res.data.data ? res.data.data.length : 0)
                );
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    }, [
        brand,
        page,
        itemsPerPage,
        sortBy,
        debouncedSearch,
        selectedCategory,
        selectedSubcategory,
        debouncedPriceRange
    ]);

    const handleClearFilters = () => {
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setIsPriceRangeManuallySet(false);
        setPriceRange([0, 10000]);
        setSortBy("default");
        setSearchTerm("");
        setPage(1);
    };

    // ... inside BrandPage ...
    return (
        <Box sx={{ backgroundColor: "#fcfcfc", minHeight: "100vh" }}>
            <SEO
                title={brand?.name}
                description={brand ? `Compra productos de ${brand.name} en MerryColor. Envíos a todo México.` : "Marca exclusiva en MerryColor."}
                image={brand?.image}
                url={`/marca/${brandSlug}`}
            />
            <Header1 />

            {/* Hero Section - White to Rose Blur */}
            <Box
                sx={{
                    background: "linear-gradient(180deg, #fff 0%, #fff0f5 100%)",
                    pt: { xs: 4, md: 6 },
                    pb: { xs: 6, md: 8 },
                    px: 2,
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* Blur Blobs */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "-20%",
                        right: "-10%",
                        width: "600px",
                        height: "600px",
                        background: "radial-gradient(circle, rgba(233,69,96,0.08) 0%, transparent 70%)",
                        filter: "blur(60px)",
                        pointerEvents: "none",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "-20%",
                        left: "-10%",
                        width: "500px",
                        height: "500px",
                        background: "radial-gradient(circle, rgba(128,31,86,0.06) 0%, transparent 70%)",
                        filter: "blur(50px)",
                        pointerEvents: "none",
                    }}
                />

                <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>
                    <Link
                        to="/catalogo"
                        style={{
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            color: "#801F56",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            marginBottom: 24,
                            transition: "transform 0.2s",
                        }}
                    >
                        <ArrowBackRounded sx={{ fontSize: 18 }} />
                        Volver
                    </Link>

                    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: 4 }}>
                        {brand?.image ? (
                            <Box
                                component="img"
                                src={brand.image}
                                alt={brand?.name}
                                sx={{
                                    width: { xs: 120, md: 140 },
                                    height: { xs: 120, md: 140 },
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    border: "4px solid #fff",
                                    boxShadow: "0 12px 30px rgba(128,31,86,0.15)",
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: { xs: 120, md: 140 },
                                    height: { xs: 120, md: 140 },
                                    borderRadius: "50%",
                                    background: "linear-gradient(135deg, #801F56, #e94560)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "4px solid #fff",
                                    boxShadow: "0 12px 30px rgba(128,31,86,0.15)",
                                }}
                            >
                                <StorefrontRounded sx={{ color: "#fff", fontSize: 50 }} />
                            </Box>
                        )}

                        <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                            <Typography
                                variant="h1"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                                    letterSpacing: "-1px",
                                    lineHeight: 1,
                                    mb: 1,
                                    color: "#111",
                                }}
                            >
                                {brand?.name || "Cargando..."}
                            </Typography>
                            <Typography sx={{ color: "#666", fontSize: "1.1rem", fontWeight: 500 }}>
                                Colección Exclusiva • {totalProducts} productos
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Main Layout */}
            <Container maxWidth="xl" sx={{ mt: -4, position: "relative", zIndex: 10, pb: 8 }}>
                <Grid container spacing={4}>
                    {/* Sidebar Filters */}
                    <Grid item xs={12} md={3}>
                        <Box sx={{ position: "sticky", top: 120 }}>
                            <FilterPanel
                                // Categories
                                categories={categories}
                                selectedCategory={selectedCategory}
                                onCategoryChange={(val) => { setSelectedCategory(val); setPage(1); }}
                                subcategories={subcategories}
                                selectedSubcategory={selectedSubcategory}
                                onSubcategoryChange={(val) => { setSelectedSubcategory(val); setPage(1); }}

                                // Brand Navigation
                                brands={brands}
                                selectedBrand={brand?.id}
                                onBrandChange={(val) => {
                                    const b = brands.find(br => br.id === val);
                                    if (b) {
                                        history.push(`/marca/${b.name.toLowerCase().replace(/\s+/g, '-')}`);
                                    } else {
                                        // "Todas las marcas" (val is null) -> Go to Catalog
                                        history.push({
                                            pathname: '/catalogo',
                                            state: { scrollToSearch: true }
                                        });
                                    }
                                }}

                                // Price
                                minPrice={0}
                                maxPrice={10000}
                                priceRange={priceRange}
                                onPriceChange={(val, manual) => { setPriceRange(val); if (manual) setIsPriceRangeManuallySet(true); }}

                                // Sort
                                sortBy={sortBy}
                                onSortChange={setSortBy}

                                // Clear
                                onClearFilters={handleClearFilters}

                                // Mobile
                                isMobile={isMobile}
                                isOpen={filterPanelOpen}
                                onToggle={() => setFilterPanelOpen(!filterPanelOpen)}
                            />
                        </Box>
                    </Grid>

                    {/* Products Column */}
                    <Grid item xs={12} md={9}>
                        {/* Search & Mobile Filter Toggle */}
                        <Box sx={{ mb: 4, display: "flex", gap: 2 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    backgroundColor: "#fff",
                                    borderRadius: "12px",
                                    px: 2,
                                    py: 1,
                                    border: "1px solid #eee",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                                    transition: "all 0.2s",
                                    "&:focus-within": {
                                        borderColor: "#801F56",
                                        boxShadow: "0 4px 12px rgba(128,31,86,0.1)",
                                    }
                                }}
                            >
                                <SearchRounded sx={{ color: "#aaa", mr: 1.5 }} />
                                <InputBase
                                    placeholder={`Buscar en ${brand?.name || "esta marca"}...`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{ flex: 1, fontSize: "1rem" }}
                                />
                                {searchTerm && (
                                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                                        <CloseRounded fontSize="small" />
                                    </IconButton>
                                )}
                            </Paper>

                            {isMobile && (
                                <IconButton
                                    onClick={() => setFilterPanelOpen(true)}
                                    sx={{
                                        backgroundColor: "#111",
                                        color: "#fff",
                                        borderRadius: "12px",
                                        width: 56,
                                        '&:hover': { backgroundColor: "#333" }
                                    }}
                                >
                                    <FilterListRounded />
                                </IconButton>
                            )}
                        </Box>

                        {/* Grid */}
                        {loading ? (
                            <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
                                <CircularProgress sx={{ color: "#801F56" }} />
                            </Box>
                        ) : products.length > 0 ? (
                            <Fade in={true}>
                                <Box>
                                    <Grid container spacing={3}>
                                        {products.map((product) => (
                                            <Grid item xs={6} md={4} lg={4} key={product.id}>
                                                <CardProduct product={product} />
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {totalProducts > itemsPerPage && (
                                        <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
                                            <Pagination
                                                count={Math.ceil(totalProducts / itemsPerPage)}
                                                page={page}
                                                onChange={(e, v) => { setPage(v); window.scrollTo({ top: 300, behavior: 'smooth' }); }}
                                                color="primary"
                                                size="large"
                                                sx={{
                                                    '& .Mui-selected': {
                                                        backgroundColor: '#801F56 !important',
                                                    }
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Fade>
                        ) : (
                            <Box sx={{ textAlign: "center", py: 8 }}>
                                <Typography variant="h6" color="textSecondary">No se encontraron productos.</Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Container>

            <Footer />
        </Box>
    );
};

export default BrandPage;
